import { defineStore } from 'pinia'
import { ref } from 'vue'

import client, { ensureCsrfCookie, isUnauthenticated } from '@/api/client'
import type { Resource, User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)

  /**
   * `null` significa "ainda não perguntamos ao servidor", que é diferente de
   * "não está logado". Sem essa distinção, um F5 numa página protegida joga o
   * usuário para o login antes de a verificação terminar.
   */
  const carregado = ref(false)

  async function carregarUsuario(): Promise<void> {
    try {
      const { data } = await client.get<Resource<User>>('/me')
      user.value = data.data
    } catch (error) {
      if (!isUnauthenticated(error)) {
        throw error
      }
      user.value = null
    } finally {
      carregado.value = true
    }
  }

  async function entrar(email: string, password: string): Promise<void> {
    await ensureCsrfCookie()

    const { data } = await client.post<Resource<User>>('/login', {
      email,
      password,
    })

    user.value = data.data
    carregado.value = true
  }

  async function sair(): Promise<void> {
    await client.post('/logout')
    user.value = null
  }

  /**
   * Conveniência de interface para esconder botões e itens de menu.
   *
   * NÃO é autorização: quem decide é o Gate no servidor, em cada rota. Esconder o
   * botão evita o clique frustrado; o 403 evita o acesso.
   */
  function pode(permissao: string): boolean {
    return user.value?.permissoes.includes(permissao) ?? false
  }

  return { user, carregado, pode, carregarUsuario, entrar, sair }
})
